/* eslint-disable @typescript-eslint/no-explicit-any */
/** Per-user Settings (profile, e-signature, CV stamping, training certificates)
 * + System page. The My Profile page is SERVER-BACKED: fields live in
 * User.profileJson, CVs and certificates are real uploaded files
 * (/api/users/me/*) — with debounced autosave plus an explicit "Save changes"
 * button so users can make sure their edits are stored. */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useData } from '../context/AppDataContext';
import { useCan } from '../context/AuthContext';
import { Ico, Avatar, useToast, avatars, downscaleImage } from '../components/ui';
import { apiClient } from '../lib/apiClient';

/** Today in the unit's business timezone (Thailand — matches the server's
 *  stamps; toISOString would be UTC and lag by 7 hours). */
function bangkokToday(offsetDays = 0): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Bangkok' }).format(new Date(Date.now() + offsetDays * 86400000));
}

/** Validity chip for a certificate expiry (ISO yyyy-mm-dd). */
function certStatus(expiry: string): { tone: string; label: string } {
  if (!expiry) return { tone: 'slate', label: 'No expiry' };
  if (expiry < bangkokToday()) return { tone: 'red', label: 'Expired' };
  if (expiry <= bangkokToday(90)) return { tone: 'amber', label: 'Expires soon' };
  return { tone: 'green', label: 'Valid' };
}

/** A complete, sane expiry date (date inputs emit zero-padded partial years
 *  like 0002-08-01 while typing — never persist those). */
const isSaneDate = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v) && v >= '1990-01-01' && v <= '2100-12-31';

const STANDARD_CERTS = ['Good Clinical Practice (GCP)', 'Human Subject Protection (HSP)', 'Responsible Conduct of Research'];

const NOTIFY_DEFAULTS: Array<[string, boolean, boolean]> = [
  ['Task assigned to me', true, true],
  ['Task deadline approaching', true, true],
  ['Compensation marked received', true, false],
  ['Meeting scheduled', true, true],
  ['New document approved', false, false],
  ['Quotation approved by sponsor', true, true],
];

export function SettingsPage({ currentUser }: { currentUser: any }) {
  const toast = useToast();

  const [s, setS] = useState<any>({});
  const [cvHistory, setCvHistory] = useState<any[]>([]);
  const [certs, setCerts] = useState<any[]>([]);
  // Tri-state: inputs stay DISABLED until the real profile arrived — a
  // writable empty form over unloaded data would autosave blanks over the
  // saved profile.
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const loaded = loadState === 'ready';
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sRef = useRef<any>({});
  sRef.current = s;
  const [loadNonce, setLoadNonce] = useState(0); // bump to retry a failed load

  // Self-service password change (Security card). Independent of the profile
  // autosave — an explicit button, validated client-side before the API call.
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoadState('loading');
    apiClient.users.me.profile()
      .then((res) => {
        if (!alive) return;
        setS(res.profile || {});
        setCvHistory(res.cvHistory || []);
        setCerts(res.certificates || []);
        setLoadState('ready');
      })
      .catch(() => { if (alive) setLoadState('error'); });
    return () => {
      alive = false;
      // Pending autosave dies with the page — the session may be gone by the
      // time it would fire; the explicit Save button exists for "make sure".
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser.id, loadNonce]);

  const saveProfile = async (explicit = false) => {
    if (autosaveTimer.current) { clearTimeout(autosaveTimer.current); autosaveTimer.current = null; }
    setSaving(true);
    try {
      await apiClient.users.me.saveProfile(sRef.current);
      // A newer edit may have been typed while the PUT was in flight — only
      // clear the dirty flag when no autosave is pending for it.
      if (!autosaveTimer.current) setDirty(false);
      setSavedAt(new Date().toTimeString().slice(0, 5));
      if (explicit) toast('Profile saved ✓');
    } catch {
      if (explicit) toast('Could not save your profile — please try again.');
    } finally { setSaving(false); }
  };

  /** Self-service password change: client-side checks, then PUT /users/me/password.
   *  Server re-verifies the current password and rejects a same-as-current change. */
  const changePassword = async () => {
    setPwError(null);
    if (!pw.current || !pw.next || !pw.confirm) { setPwError('Please fill in all three password fields.'); return; }
    if (pw.next.length < 8) { setPwError('New password must be at least 8 characters.'); return; }
    if (pw.next !== pw.confirm) { setPwError('New password and confirmation do not match.'); return; }
    if (pw.next === pw.current) { setPwError('New password must be different from your current password.'); return; }
    setPwBusy(true);
    try {
      await apiClient.users.me.changePassword({ currentPassword: pw.current, newPassword: pw.next });
      setPw({ current: '', next: '', confirm: '' });
      toast('Password updated ✓');
    } catch (err: any) {
      setPwError(err?.body?.message || 'Could not change your password — please try again.');
    } finally { setPwBusy(false); }
  };

  /** Update a profile field; autosaves ~2s after the last keystroke. The
   *  top-right "Save changes" button saves immediately and confirms.
   *  No-op until the profile actually loaded (never save over unloaded data). */
  const upd = (k: string, v: any) => {
    if (!loaded) return;
    setS((prev: any) => ({ ...prev, [k]: v }));
    setDirty(true);
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => { autosaveTimer.current = null; void saveProfile(false); }, 2000);
  };

  const fileRef = useRef<HTMLInputElement>(null);
  const avatarRef = useRef<HTMLInputElement>(null);
  const certFileRef = useRef<HTMLInputElement>(null);
  const [certReplacing, setCertReplacing] = useState<string | null>(null);
  /** Value of the cert field being edited, snapshotted on focus — the blur
   *  dirty-check must compare against the SERVER value, not the live state. */
  const certFieldOnFocus = useRef<string>('');
  const sigCanvasRef = useRef<HTMLCanvasElement>(null);

  const onAvatarPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast('Please choose an image file'); return; }
    try {
      const dataUrl = await downscaleImage(f, 128);
      avatars.set(currentUser.id, dataUrl);
      const kb = Math.round((dataUrl.length * 3 / 4) / 1024);
      toast(`Avatar updated · downscaled to 128px (~${kb} KB)`);
    } catch { toast('Could not process that image'); }
  };
  const removeAvatar = () => { avatars.remove(currentUser.id); toast('Avatar removed'); };

  /* ---------- CV upload (real save: file → server, stamped + hashed) ---------- */
  const [cvBusy, setCvBusy] = useState(false);
  const uploadCv = async (f: File) => {
    if (cvBusy) return;
    if (!/\.pdf$/i.test(f.name)) { toast('The CV must be a PDF file'); return; }
    setCvBusy(true);
    try {
      const entry = await apiClient.users.me.uploadCv(f);
      setCvHistory((h) => [entry, ...h.map((c) => ({ ...c, current: false }))]);
      toast(`CV stamped & saved · ${entry.stampedAt} · SHA-256 ${entry.sha256.slice(0, 10)}…`);
    } catch (err: any) {
      toast(err?.body?.message || 'CV upload failed — please try again.');
    } finally { setCvBusy(false); }
  };
  const onUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (f) void uploadCv(f);
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    (e.currentTarget as HTMLElement).style.background = '';
    const f = e.dataTransfer.files?.[0];
    if (f) void uploadCv(f);
  };

  /* ---------- Training certificates (per-person CRUD + re-upload) ---------- */
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<{ name: string; expiry: string; file: File | null }>({ name: '', expiry: '', file: null });
  const addFileRef = useRef<HTMLInputElement>(null);

  const addCert = async () => {
    if (!addForm.name.trim() || !addForm.expiry) { toast('Certificate name and expiry date are required'); return; }
    try {
      const row = await apiClient.users.me.addCertificate({ name: addForm.name.trim(), expiry: addForm.expiry, file: addForm.file ?? undefined });
      setCerts((c) => [...c, row]);
      setAddOpen(false);
      setAddForm({ name: '', expiry: '', file: null });
      toast(`Certificate added · ${row.name}`);
    } catch (err: any) { toast(err?.body?.message || 'Could not add the certificate.'); }
  };

  const patchCert = async (id: string, patch: { name?: string; expiry?: string; file?: File }) => {
    try {
      const row = await apiClient.users.me.updateCertificate(id, patch);
      setCerts((c) => c.map((x) => (x.id === row.id ? row : x)));
      if (patch.file) toast(`Certificate re-uploaded · ${row.fileName}`);
    } catch (err: any) { toast(err?.body?.message || 'Could not update the certificate.'); }
  };

  const removeCert = async (id: string, name: string) => {
    if (!window.confirm(`Remove certificate "${name}"?`)) return;
    try {
      await apiClient.users.me.deleteCertificate(id);
      setCerts((c) => c.filter((x) => x.id !== id));
      toast('Certificate removed');
    } catch { toast('Could not remove the certificate.'); }
  };

  const onCertFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (f && certReplacing) void patchCert(certReplacing, { file: f });
    setCertReplacing(null);
  };

  /* ---------- E-signature drawing ---------- */
  const [drawing, setDrawing] = useState(false);
  const stroked = useRef(false); // a bare click must not replace the saved signature
  const startDraw = (e: React.MouseEvent) => {
    setDrawing(true);
    const c = sigCanvasRef.current;
    if (!c) return;
    const r = c.getBoundingClientRect();
    const ctx = c.getContext('2d')!;
    ctx.strokeStyle = '#1A1F2E'; ctx.lineWidth = 2; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(e.clientX - r.left, e.clientY - r.top);
  };
  const drawTo = (e: React.MouseEvent) => {
    if (!drawing) return;
    stroked.current = true;
    const c = sigCanvasRef.current!;
    const r = c.getBoundingClientRect();
    const ctx = c.getContext('2d')!;
    ctx.lineTo(e.clientX - r.left, e.clientY - r.top); ctx.stroke();
  };
  const stopDraw = () => {
    if (!drawing) return;
    setDrawing(false);
    if (!stroked.current || !loaded) return; // click without movement → keep the saved signature
    stroked.current = false;
    const c = sigCanvasRef.current!;
    setS((prev: any) => ({ ...prev, signature: c.toDataURL('image/png'), sigStyle: 'drawn' }));
    setDirty(true);
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => { autosaveTimer.current = null; void saveProfile(false); }, 2000);
  };
  const clearSig = () => {
    const c = sigCanvasRef.current;
    if (c) c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
    upd('signature', '');
  };
  /** Switching styles must not carry a mismatched value across — a drawn
   *  data-URL pretending to be a "typed" name would end up as UserCv.signedBy. */
  const setSigStyle = (style: 'typed' | 'drawn' | 'image') => {
    if (!loaded) return;
    setS((prev: any) => {
      const isData = String(prev.signature || '').startsWith('data:');
      // Keep a data-URL when switching between the two image-bearing styles
      // (drawn ↔ image); only clear when the value can't apply to the new style.
      const keepData = (style === 'drawn' || style === 'image') && isData;
      const keepTyped = style === 'typed' && prev.signature && !isData;
      const signature = keepData || keepTyped ? prev.signature : '';
      return { ...prev, sigStyle: style, signature };
    });
    setDirty(true);
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => { autosaveTimer.current = null; void saveProfile(false); }, 2000);
  };
  /** Upload a signature IMAGE from the PC (senior request): downscale to a small
   *  data-URL (max 540×180, fits the profileJson cap) and store as sigStyle 'image'. */
  const uploadSigImage = (file: File) => {
    if (!file || !/^image\//.test(file.type)) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const maxW = 540, maxH = 180;
        const scale = Math.min(1, maxW / img.width, maxH / img.height);
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const cv = document.createElement('canvas');
        cv.width = w; cv.height = h;
        const ctx = cv.getContext('2d')!;
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = cv.toDataURL('image/png');
        setS((prev: any) => ({ ...prev, signature: dataUrl, sigStyle: 'image' }));
        setDirty(true);
        if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
        autosaveTimer.current = setTimeout(() => { autosaveTimer.current = null; void saveProfile(false); }, 1200);
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  };
  // Rehydrate the canvas with the saved drawn signature so a touch-up adds to
  // it instead of replacing it with just the new stroke on a blank canvas.
  useEffect(() => {
    if (!loaded || (s.sigStyle || 'typed') !== 'drawn') return;
    const c = sigCanvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d')!;
    ctx.clearRect(0, 0, c.width, c.height);
    const sig = String(s.signature || '');
    if (sig.startsWith('data:')) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0);
      img.src = sig;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, s.sigStyle]);

  const currentCv = cvHistory.find((c: any) => c.current);
  const sigStyle = s.sigStyle || 'typed';
  const typedSig = sigStyle === 'typed' && s.signature && !String(s.signature).startsWith('data:') ? s.signature : '';
  const notify = (label: string, inAppDefault: boolean, emailDefault: boolean) =>
    (s.notify && s.notify[label]) || { inApp: inAppDefault, email: emailDefault };

  return (
    <div className="page-pad fadein">
      <div className="page-head">
        <div>
          <h2>My Profile</h2>
          <div className="sub">Manage your profile, security, e-signature, and CV — {currentUser.name}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
            {saving ? 'Saving…' : dirty ? 'Unsaved changes' : savedAt ? `All changes saved · ${savedAt}` : loaded ? 'Auto-saves as you type' : 'Loading…'}
          </span>
          <button className="btn primary" disabled={saving || !loaded} onClick={() => void saveProfile(true)}><Ico.check /> {saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 20 }}>
        <div className="card" style={{ alignSelf: 'flex-start', position: 'sticky', top: 8 }}>
          <div className="card-b" style={{ padding: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[
              ['profile', 'Personal details'],
              ['security', 'Security'],
              ['signature', 'E-signature'],
              ['cv', 'CV upload'],
              ['certs', 'Training certificates'],
              ['notify', 'Notifications'],
            ].map(([id, label]) => (
              <a key={id} href={'#' + id} style={{ padding: '7px 10px', borderRadius: 6, fontSize: 12.5, color: 'var(--text)', textDecoration: 'none' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--surface-alt)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ''; }}>
                {label}
              </a>
            ))}
          </div>
        </div>

        {loadState !== 'ready' ? (
          <div className="card" style={{ alignSelf: 'flex-start', width: '100%' }}>
            <div className="card-b" style={{ padding: 32, textAlign: 'center', color: 'var(--text-2)', fontSize: 13 }}>
              {loadState === 'loading' ? (
                'Loading your profile…'
              ) : (
                <>
                  <div style={{ marginBottom: 10 }}>Could not load your profile — editing is disabled so nothing gets overwritten.</div>
                  <button className="btn primary sm" onClick={() => setLoadNonce((n) => n + 1)}>Retry</button>
                </>
              )}
            </div>
          </div>
        ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div id="profile" className="card">
            <div className="card-h"><h3>Personal details</h3><span className="chip" data-tone="slate"><span className="dot" />Visible to your team</span></div>
            <div className="card-b">
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                <Avatar user={currentUser} size="lg" />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{currentUser.name}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{currentUser.role} · {currentUser.email}</div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    <input ref={avatarRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onAvatarPick} />
                    <button className="btn sm" onClick={() => avatarRef.current?.click()}>Change avatar</button>
                    <button className="btn ghost sm" onClick={removeAvatar}>Remove</button>
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 4 }}>JPG/PNG · auto-cropped square &amp; downscaled to 128px to save storage.</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="field"><label>Full name (managed by admin)</label><input value={currentUser.name} disabled /></div>
                <div className="field"><label>Gmail address (sign-in identity)</label><input value={currentUser.email} disabled /></div>
                <div className="field"><label>Phone</label><input value={s.phone ?? ''} onChange={(e) => upd('phone', e.target.value)} placeholder="+66 …" /></div>
                <div className="field"><label>Position</label><input value={s.position ?? ''} onChange={(e) => upd('position', e.target.value)} placeholder={currentUser.role} /></div>
                <div className="field" style={{ gridColumn: 'span 2' }}><label>Affiliation</label><input value={s.affiliation ?? ''} onChange={(e) => upd('affiliation', e.target.value)} placeholder="BioCE, Faculty of Medicine, Chiang Mai University" /></div>
                <div className="field" style={{ gridColumn: 'span 2' }}><label>Area of expertise</label><input value={s.expertise ?? ''} onChange={(e) => upd('expertise', e.target.value)} /></div>
                <div className="field" style={{ gridColumn: 'span 2' }}><label>Short bio</label><textarea value={s.bio ?? ''} onChange={(e) => upd('bio', e.target.value)} /></div>
              </div>
            </div>
          </div>

          <div id="security" className="card">
            <div className="card-h"><h3>Security</h3></div>
            <div className="card-b" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                <div className="field"><label>Current password</label><input type="password" autoComplete="current-password" value={pw.current} onChange={(e) => { setPw((p) => ({ ...p, current: e.target.value })); setPwError(null); }} disabled={pwBusy} /></div>
                <div className="field"><label>New password</label><input type="password" autoComplete="new-password" placeholder="At least 8 characters" value={pw.next} onChange={(e) => { setPw((p) => ({ ...p, next: e.target.value })); setPwError(null); }} disabled={pwBusy} /></div>
                <div className="field"><label>Confirm new password</label><input type="password" autoComplete="new-password" value={pw.confirm} onChange={(e) => { setPw((p) => ({ ...p, confirm: e.target.value })); setPwError(null); }} disabled={pwBusy} /></div>
              </div>
              {pwError && (
                <div role="alert" style={{ padding: 10, background: 'var(--accent-soft)', borderRadius: 6, fontSize: 11.5, color: 'var(--accent)' }}>{pwError}</div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button className="btn primary sm" disabled={pwBusy} onClick={() => void changePassword()}><Ico.check /> {pwBusy ? 'Updating…' : 'Update password'}</button>
                <div style={{ fontSize: 11.5, color: 'var(--text-2)' }}>
                  Changing your password takes effect immediately. Devices already signed in stay signed in until their session expires.
                </div>
              </div>
            </div>
          </div>

          <div id="signature" className="card">
            <div className="card-h"><h3>E-signature</h3><span className="chip" data-tone="slate"><span className="dot" />Applied to your CV &amp; signed documents</span></div>
            <div className="card-b">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                    <div className="seg">
                      <button className={sigStyle === 'typed' ? 'on' : ''} onClick={() => setSigStyle('typed')}>Typed</button>
                      <button className={sigStyle === 'drawn' ? 'on' : ''} onClick={() => setSigStyle('drawn')}>Drawn</button>
                      <button className={sigStyle === 'image' ? 'on' : ''} onClick={() => setSigStyle('image')}>Image</button>
                    </div>
                  </div>
                  {sigStyle === 'typed' ? (
                    <div className="field">
                      <label>Type your name in the signature font</label>
                      <input
                        value={typedSig}
                        placeholder={currentUser.name}
                        style={{ fontFamily: '"Brush Script MT", "Lucida Handwriting", cursive', fontSize: 24, padding: 12 }}
                        onChange={(e) => upd('signature', e.target.value)}
                      />
                      <div style={{ fontSize: 10.5, color: 'var(--text-2)', marginTop: 4 }}>This is your legal e-signature for HEROs documents.</div>
                    </div>
                  ) : sigStyle === 'image' ? (
                    <div className="field">
                      <label>Upload a signature image from your PC</label>
                      <input type="file" accept="image/png,image/jpeg" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadSigImage(f); e.currentTarget.value = ''; }} />
                      <div style={{ fontSize: 10.5, color: 'var(--text-2)', marginTop: 4 }}>PNG or JPG. The image is downscaled and saved as your e-signature (shown in the preview →).</div>
                      {s.signature && String(s.signature).startsWith('data:') && (
                        <div style={{ marginTop: 8 }}><img src={s.signature} style={{ maxHeight: 70, border: '1px solid var(--border)', borderRadius: 6, background: '#fff', padding: 4 }} /></div>
                      )}
                    </div>
                  ) : (
                    <div className="field">
                      <label>Draw your signature in the box</label>
                      <div style={{ position: 'relative', height: 120, border: '1px dashed var(--border-strong)', borderRadius: 6, background: '#FBFBF7', overflow: 'hidden' }}>
                        <canvas
                          ref={sigCanvasRef}
                          width="540" height="118"
                          style={{ width: '100%', height: '100%', display: 'block', cursor: 'crosshair' }}
                          onMouseDown={startDraw}
                          onMouseMove={drawTo}
                          onMouseUp={stopDraw}
                          onMouseLeave={stopDraw}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                        <button className="btn ghost sm" onClick={clearSig}><Ico.trash /> Clear</button>
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-2)', marginBottom: 6 }}>Preview as it will appear on stamped documents:</div>
                  <div style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 16, background: '#fff', minHeight: 120, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                    {sigStyle === 'typed' ? (
                      <div style={{ fontFamily: '"Brush Script MT", "Lucida Handwriting", cursive', fontSize: 30, color: 'var(--navy)', borderBottom: '1px solid var(--text-3)', paddingBottom: 4, marginBottom: 6 }}>
                        {typedSig || currentUser.name}
                      </div>
                    ) : s.signature && String(s.signature).startsWith('data:') ? (
                      <img src={s.signature} style={{ maxHeight: 70, alignSelf: 'flex-start', borderBottom: '1px solid var(--text-3)', paddingBottom: 4, marginBottom: 6 }} />
                    ) : (
                      <div style={{ fontSize: 12, color: 'var(--text-3)', fontStyle: 'italic', borderBottom: '1px solid var(--text-3)', paddingBottom: 4, marginBottom: 6 }}>
                        — no signature yet —
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--text-2)' }}>
                      <div><strong>{currentUser.name}</strong> · {currentUser.role}</div>
                      <div className="mono" style={{ fontSize: 10.5, color: 'var(--text-3)', marginTop: 2 }}>{currentUser.email}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div id="cv" className="card">
            <div className="card-h">
              <div>
                <h3>CV upload</h3>
                <div className="sub">When you upload, the platform automatically stamps the PDF with a timestamp and your e-signature, then saves it to your profile.</div>
              </div>
              <button className="btn primary sm" disabled={cvBusy} onClick={() => fileRef.current?.click()}><Ico.plus /> {cvBusy ? 'Uploading…' : 'Upload new CV'}</button>
              <input ref={fileRef} type="file" accept=".pdf" hidden onChange={onUpload} />
            </div>
            <div className="card-b">
              <div
                onDragOver={(e) => { e.preventDefault(); (e.currentTarget as HTMLElement).style.background = 'var(--surface-alt)'; }}
                onDragLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ''; }}
                onDrop={onDrop}
                style={{ border: '2px dashed var(--border-strong)', borderRadius: 8, padding: 24, textAlign: 'center', cursor: 'pointer', transition: 'background 0.15s', opacity: cvBusy ? 0.6 : 1 }}
                onClick={() => !cvBusy && fileRef.current?.click()}>
                <Ico.dl />
                <div style={{ marginTop: 8, fontSize: 13, fontWeight: 500 }}>{cvBusy ? 'Uploading & stamping…' : 'Drop your CV PDF here, or click to browse'}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-2)', marginTop: 4 }}>
                  The platform stamps the file with: <strong>your e-signature</strong> · <strong>timestamp</strong> · <strong>SHA-256 hash</strong> — and stores it on the server.
                </div>
              </div>

              {currentCv && (
                <div style={{ marginTop: 16, padding: 12, border: '1px solid #4a8a55', borderRadius: 8, background: 'rgba(74,138,85,0.05)' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: '0 0 60px', height: 78, background: '#fff', border: '1px solid var(--border)', borderRadius: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 4, position: 'relative' }}>
                      <div style={{ fontSize: 8.5, fontWeight: 700, color: 'var(--accent)' }}>PDF</div>
                      <div style={{ width: '100%', height: 2, background: 'var(--accent-soft)', margin: '3px 0' }} />
                      {[0, 1, 2, 3].map((i) => <div key={i} style={{ width: '80%', height: 1, background: 'var(--border)', marginTop: 1 }} />)}
                      <div style={{ position: 'absolute', bottom: 2, right: 2, fontSize: 14, color: '#4a8a55' }}>✓</div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="mono" style={{ fontSize: 12.5, fontWeight: 600 }}>{currentCv.fileName}</span>
                        <span className="chip" data-tone="green"><span className="dot" />Current</span>
                        <span className="chip" data-tone="blue"><span className="dot" />Signed</span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 4, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 12px', maxWidth: 480 }}>
                        <span>Stamped at:</span><span className="mono">{currentCv.stampedAt}</span>
                        <span>Signed by:</span><span>{currentCv.signedBy} ({currentUser.email})</span>
                        <span>SHA-256:</span><span className="mono">{String(currentCv.sha256).slice(0, 16)}…</span>
                        <span>Size:</span><span className="mono">{currentCv.size}</span>
                        {currentCv.origName && <><span>Original:</span><span className="mono">{currentCv.origName}</span></>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <a className="btn sm" href={apiClient.users.me.cvFileUrl(currentCv.id)} download={currentCv.fileName}><Ico.dl /> Download</a>
                      <button className="btn ghost sm" onClick={() => window.open(apiClient.users.me.cvFileUrl(currentCv.id), '_blank', 'noopener')}>Preview</button>
                    </div>
                  </div>
                </div>
              )}

              {cvHistory.length > 1 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, marginBottom: 6 }}>Previous versions ({cvHistory.length - 1})</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {cvHistory.filter((c: any) => !c.current).map((c: any) => (
                      <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'var(--surface-alt)', borderRadius: 4, fontSize: 11.5 }}>
                        <Ico.doc />
                        <span className="mono">{c.fileName}</span>
                        <span style={{ color: 'var(--text-2)', marginLeft: 'auto' }} className="mono">{c.stampedAt}</span>
                        <span className="mono" style={{ color: 'var(--text-3)', fontSize: 10 }}>{String(c.sha256).slice(0, 10)}…</span>
                        <a className="btn ghost icon sm" href={apiClient.users.me.cvFileUrl(c.id)} download={c.fileName}><Ico.dl /></a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {loaded && cvHistory.length === 0 && (
                <div style={{ marginTop: 12, padding: 10, background: 'var(--accent-soft)', borderRadius: 6, fontSize: 11.5, color: 'var(--accent)' }}>
                  ⚠ No CV on file. Upload one to complete your profile.
                </div>
              )}
            </div>
          </div>

          <div id="certs" className="card">
            <div className="card-h">
              <h3>Training certificates</h3>
              <span className="chip" data-tone="slate"><span className="dot" />Re-uploaded annually</span>
            </div>
            <div className="card-b" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input ref={certFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" hidden onChange={onCertFilePick} />
              {certs.map((c: any) => {
                const st = certStatus(c.expiry);
                return (
                  <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '1fr 150px 120px 90px 30px', alignItems: 'center', gap: 12, padding: 8, border: '1px solid var(--border)', borderRadius: 6 }}>
                    <div>
                      <input
                        value={c.name}
                        onFocus={(e) => { certFieldOnFocus.current = e.target.value; }}
                        onChange={(e) => setCerts((all) => all.map((x) => (x.id === c.id ? { ...x, name: e.target.value } : x)))}
                        onBlur={(e) => {
                          // Compare against the value snapshotted on FOCUS — the
                          // row state already holds every keystroke, so c.name
                          // would always equal the typed value here.
                          const v = e.target.value.trim();
                          if (v && v !== certFieldOnFocus.current) void patchCert(c.id, { name: v });
                          else if (!v) setCerts((all) => all.map((x) => (x.id === c.id ? { ...x, name: certFieldOnFocus.current } : x)));
                        }}
                        style={{ fontSize: 12.5, fontWeight: 500, border: 'none', background: 'transparent', padding: '2px 0', width: '100%' }}
                      />
                      {c.fileName ? (
                        <a className="mono" href={apiClient.users.me.certFileUrl(c.id)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10.5, color: 'var(--text-2)' }}>{c.fileName}</a>
                      ) : (
                        <span className="mono" style={{ fontSize: 10.5, color: 'var(--text-3)' }}>no file uploaded yet</span>
                      )}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span>Expires</span>
                      <input
                        type="date"
                        value={c.expiry}
                        onFocus={(e) => { certFieldOnFocus.current = e.target.value; }}
                        onChange={(e) => { const v = e.target.value; setCerts((all) => all.map((x) => (x.id === c.id ? { ...x, expiry: v } : x))); }}
                        onBlur={(e) => {
                          // Save only complete, sane dates on blur — date inputs
                          // emit zero-padded partial years per keystroke, which
                          // must never race each other into the DB.
                          const v = e.target.value;
                          if (isSaneDate(v) && v !== certFieldOnFocus.current) void patchCert(c.id, { expiry: v });
                          else if (!isSaneDate(v)) setCerts((all) => all.map((x) => (x.id === c.id ? { ...x, expiry: certFieldOnFocus.current } : x)));
                        }}
                        style={{ fontSize: 11, padding: '2px 4px', width: 120 }}
                      />
                    </div>
                    <span className="chip" data-tone={st.tone}><span className="dot" />{st.label}</span>
                    <button className="btn sm" onClick={() => { setCertReplacing(c.id); certFileRef.current?.click(); }}>{c.fileName ? 'Replace' : 'Upload'}</button>
                    <button className="btn ghost icon sm" title="Remove certificate" onClick={() => void removeCert(c.id, c.name)}><Ico.trash /></button>
                  </div>
                );
              })}
              {loaded && certs.length === 0 && (
                <div style={{ padding: 10, background: 'var(--surface-alt)', borderRadius: 6, fontSize: 11.5, color: 'var(--text-2)' }}>
                  No certificates yet — add your {STANDARD_CERTS.map((n) => n.match(/\(([^)]+)\)/)?.[1] ?? n).join(' / ')} below.
                </div>
              )}

              {addOpen ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px 1fr auto auto', alignItems: 'end', gap: 10, padding: 10, border: '1px dashed var(--border-strong)', borderRadius: 6 }}>
                  <div className="field" style={{ margin: 0 }}>
                    <label>Certificate name</label>
                    <input list="std-certs" value={addForm.name} onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Good Clinical Practice (GCP)" />
                    <datalist id="std-certs">{STANDARD_CERTS.map((n) => <option key={n} value={n} />)}</datalist>
                  </div>
                  <div className="field" style={{ margin: 0 }}>
                    <label>Expiry date</label>
                    <input type="date" value={addForm.expiry} onChange={(e) => setAddForm((f) => ({ ...f, expiry: e.target.value }))} />
                  </div>
                  <div className="field" style={{ margin: 0 }}>
                    <label>File (PDF/JPG/PNG, optional)</label>
                    <input ref={addFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setAddForm((f) => ({ ...f, file: e.target.files?.[0] ?? null }))} />
                  </div>
                  <button className="btn primary sm" onClick={() => void addCert()}><Ico.check /> Add</button>
                  <button className="btn ghost sm" onClick={() => { setAddOpen(false); setAddForm({ name: '', expiry: '', file: null }); }}>Cancel</button>
                </div>
              ) : (
                <button className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={() => setAddOpen(true)}><Ico.plus /> Add other certificate</button>
              )}
            </div>
          </div>

          <div id="notify" className="card">
            <div className="card-h"><h3>Notifications</h3></div>
            <div className="card-b" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {NOTIFY_DEFAULTS.map(([label, inAppDef, emailDef]) => {
                const cur = notify(label as string, inAppDef as boolean, emailDef as boolean);
                const setN = (k: 'inApp' | 'email', v: boolean) =>
                  upd('notify', { ...(s.notify || {}), [label as string]: { ...cur, [k]: v } });
                return (
                  <div key={label as string} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px', alignItems: 'center', gap: 12, padding: '6px 10px', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                    <span>{label as string}</span>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-2)' }}>
                      <input type="checkbox" checked={cur.inApp} onChange={(e) => setN('inApp', e.target.checked)} /> In-app
                    </label>
                    <label title="Email delivery is not configured on this server" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-2)', opacity: 0.5 }}>
                      <input type="checkbox" checked={cur.email} disabled title="Email delivery is not configured on this server" /> Email
                    </label>
                  </div>
                );
              })}
              <div style={{ fontSize: 11, color: 'var(--text-3)', paddingTop: 6, lineHeight: 1.5 }}>
                Email notifications are unavailable — SMTP is not configured. In-app notifications appear under the bell icon.
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

export function SystemPage({ onSignOut }: { onSignOut: () => void }) {
  const D = useData();
  const canSee = useCan('sys.users.manage') === 'allow';
  // Grey + tag a value that is NOT yet wired to real telemetry (still a placeholder).
  const Demo = ({ children }: { children: ReactNode }) => (
    <span style={{ color: 'var(--text-3)' }} title="Placeholder — not yet wired to live data">
      {children} <span style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: '0.04em', border: '1px solid var(--border)', borderRadius: 6, padding: '0 4px', verticalAlign: 'middle', textTransform: 'uppercase' }}>demo</span>
    </span>
  );

  if (!canSee) {
    return (
      <div className="page-pad fadein">
        <div className="page-head"><div><h2>System</h2><div className="sub">Deployment & operational status</div></div>
          <button className="btn" style={{ color: 'var(--accent)' }} onClick={onSignOut}><Ico.arrowR /> Sign out</button>
        </div>
        <div className="card"><div className="card-b" style={{ padding: 28, textAlign: 'center', color: 'var(--text-2)', fontSize: 13 }}>
          🔒 The System page is restricted to <strong>Admin</strong> and <strong>Super Admin</strong> roles.
        </div></div>
      </div>
    );
  }

  return (
    <div className="page-pad fadein">
      <div className="page-head">
        <div>
          <h2>System</h2>
          <div className="sub">Deployment, version, and operational status of the HEROs platform <span style={{ color: 'var(--text-3)' }}>· greyed “demo” values are placeholders not yet wired to live telemetry</span></div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" style={{ color: 'var(--accent)' }} onClick={onSignOut}><Ico.arrowR /> Sign out</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 14 }}>
        <div className="stat" style={{ background: 'var(--navy)', color: '#fff', borderColor: 'var(--navy)' }}>
          <div className="label" style={{ color: 'rgba(255,255,255,0.7)' }}>Channel</div>
          <div className="v mono" style={{ fontSize: 20 }}>MVP preview</div>
          <div className="d" style={{ color: 'rgba(255,255,255,0.7)' }}>HEROs CTMS · internal</div>
        </div>
        <div className="stat"><div className="label">Uptime</div><div className="v tabular" style={{ fontSize: 18 }}><Demo>—</Demo></div><div className="d">not yet monitored</div></div>
        <div className="stat"><div className="label">Active users</div><div className="v tabular">{D.team.length}</div><div className="d">{D.pendingUsers.length} pending</div></div>
        <div className="stat"><div className="label">Active projects</div><div className="v tabular">{D.projects.length}</div><div className="d">{D.timeline.length} phase rows</div></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div className="card">
          <div className="card-h"><h3>Application</h3></div>
          <div className="card-b">
            <dl className="kv">
              <dt>App name</dt><dd>HEROs — Clinical Trial Management System</dd>
              <dt>Release channel</dt><dd>Internal preview (MVP)</dd>
              <dt>Frontend</dt><dd>React 18.3 · Vite 5.4 · TypeScript</dd>
              <dt>Backend</dt><dd>Node 22 · Fastify 5 · Prisma 6 · PostgreSQL</dd>
              <dt>Build / commit</dt><dd><Demo>not stamped</Demo></dd>
              <dt>Last deployed</dt><dd><Demo>not tracked in-app</Demo></dd>
            </dl>
          </div>
        </div>

        <div className="card">
          <div className="card-h"><h3>Deployment</h3></div>
          <div className="card-b">
            <dl className="kv">
              <dt>Host</dt><dd>Hostinger VPS (Ubuntu 24.04)</dd>
              <dt>Public URL</dt><dd className="mono" style={{ fontSize: 11 }}>trials.bioce.cloud</dd>
              <dt>TLS</dt><dd>Cloudflare edge (uniqcret) · Let’s Encrypt (bioce.cloud)</dd>
              <dt>Web server</dt><dd>Nginx · TLS 1.3</dd>
              <dt>Process</dt><dd>PM2 cluster · 2 workers</dd>
              <dt>Reverse proxy</dt><dd>Nginx → Node :3000</dd>
              <dt>Region</dt><dd><Demo>Hostinger DC</Demo></dd>
            </dl>
          </div>
        </div>

        <div className="card">
          <div className="card-h"><h3>Storage &amp; backup</h3><span className="chip" data-tone="slate"><span className="dot" />Partly wired</span></div>
          <div className="card-b">
            <dl className="kv">
              <dt>Database</dt><dd>PostgreSQL (size <Demo>n/a</Demo>)</dd>
              <dt>File storage</dt><dd><Demo>n/a</Demo></dd>
              <dt>Documents on file</dt><dd className="tabular">{D.documents.length} · across {new Set(D.documents.map((d) => d.project)).size} project{new Set(D.documents.map((d) => d.project)).size === 1 ? '' : 's'}</dd>
              <dt>Backup</dt><dd>pg_dump on the VPS (manual + pre-deploy)</dd>
              <dt>Encryption</dt><dd>TLS in transit · <Demo>at-rest encryption</Demo></dd>
            </dl>
            <hr className="rule" />
            <h4 style={{ margin: '0 0 6px', fontSize: 12, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Monthly archives <Demo>placeholder</Demo></h4>
            <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>Automated monthly archive table, in-app restore, and GitHub off-site sync are not yet implemented — backups are taken manually via <span className="mono">pg_dump</span> on the server.</div>
          </div>
        </div>

        <div className="card">
          <div className="card-h"><h3>Security &amp; access</h3></div>
          <div className="card-b">
            <dl className="kv">
              <dt>HTTPS</dt><dd><span className="chip" data-tone="green"><span className="dot" />Enforced</span></dd>
              <dt>Auth</dt><dd>Signed httpOnly cookie · CSRF double-submit</dd>
              <dt>Rate limiting</dt><dd>On (strict on login/register)</dd>
              <dt>Audit logs</dt><dd>Append-only, enabled (retention <Demo>not configured</Demo>)</dd>
              <dt>Session timeout</dt><dd><Demo>not enforced</Demo></dd>
              <dt>Last security scan</dt><dd><Demo>none recorded</Demo></dd>
            </dl>
          </div>
        </div>

        <div className="card">
          <div className="card-h"><h3>Server health</h3><span className="chip" data-tone="slate"><span className="dot" />Not wired</span></div>
          <div className="card-b">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              {['CPU', 'Memory', 'Disk I/O'].map((l) => (
                <div key={l} style={{ background: 'var(--surface-alt)', borderRadius: 6, padding: 10 }}>
                  <div style={{ fontSize: 10.5, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{l}</div>
                  <div className="v tabular" style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}><Demo>—</Demo></div>
                </div>
              ))}
            </div>
            <hr className="rule" />
            <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>Live server metrics are read on the VPS (e.g. <span className="mono">pm2</span>, <span className="mono">htop</span>) — an in-app telemetry feed is not yet wired.</div>
          </div>
        </div>

        <div className="card">
          <div className="card-h"><h3>Support &amp; help</h3></div>
          <div className="card-b" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <a className="btn ghost" href="https://herobioce.uniqcret.com/" target="_blank" rel="noopener noreferrer" style={{ justifyContent: 'flex-start' }}><Ico.doc /> User guide (PDF)</a>
            <a className="btn ghost" href="https://herobioce.uniqcret.com/" target="_blank" rel="noopener noreferrer" style={{ justifyContent: 'flex-start' }}><Ico.flag /> Release notes</a>
            <a className="btn ghost" href="mailto:support@bioce.cmu.ac.th?subject=HEROs%20issue%20report" style={{ justifyContent: 'flex-start' }}><Ico.bell /> Report an issue</a>
            <a className="btn ghost mono" href="mailto:support@bioce.cmu.ac.th" style={{ justifyContent: 'flex-start' }}>support@bioce.cmu.ac.th</a>
            <hr className="rule" />
            <div style={{ fontSize: 11.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
              For urgent system access issues, contact the BioCE IT administrator. Refer to the user guide for feature documentation.
            </div>
          </div>
        </div>

        <div className="card" style={{ gridColumn: 'span 2' }}>
          <div className="card-h"><h3>Release history</h3><span style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: '0.04em', border: '1px solid var(--border)', borderRadius: 6, padding: '0 4px', textTransform: 'uppercase', color: 'var(--text-3)' }} title="Illustrative — no in-app version registry yet">demo</span></div>
          <div className="card-b flush" style={{ color: 'var(--text-3)' }}>
            <table>
              <thead><tr><th>Version</th><th>Date</th><th>Highlights</th></tr></thead>
              <tbody>
                {[
                  { v: 'v0.4.0', d: '2026-04-12', h: 'My Profile · system page · landmark gantt dropdown · exact-date precision in Gantt' },
                  { v: 'v0.3.0', d: '2026-03-28', h: 'Compensation per-split receipts · My Earnings · Payments tracker · My Tasks' },
                  { v: 'v0.2.0', d: '2026-03-04', h: 'Quotation engine · admin pricing library · users & permissions matrix' },
                  { v: 'v0.1.0', d: '2026-02-10', h: 'Initial release — projects, timeline, monthly dashboard, documents' },
                ].map((r) => (
                  <tr key={r.v}>
                    <td className="mono" style={{ fontWeight: 600 }}>{r.v}</td>
                    <td className="mono" style={{ color: 'var(--text-2)' }}>{r.d}</td>
                    <td style={{ fontSize: 12 }}>{r.h}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
